// Phase 34c.3 — Cap table pie chart for the Partnership Detail page.
//
// Visualises the cap table as a donut where each slice = a member sized
// by ownership_pct. Sponsor gets indigo; LPs get a deterministic palette
// (sky, emerald, violet, amber, cyan, fuchsia, lime, rose, slate) so the
// same member keeps the same colour across re-renders.
//
// Special slice: "Unallocated" rendered in light slate-200 when the sum
// of ownership_pct is < 100 — important because cap tables don't always
// hit exactly 100% during the open phase.
//
// Centre text: total ownership_pct summed across all real members.
//
// Responsive: at narrow widths (< 480px) Recharts auto-scales; below
// 320px we fall back to a horizontal stacked bar via CSS so the chart
// never overflows.

import { useMemo, useState } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";

const SPONSOR_COLOR = "#4F46E5"; // indigo-600
const UNALLOCATED_COLOR = "#E2E8F0"; // slate-200
const LP_PALETTE = [
  "#0EA5E9", // sky-500
  "#10B981", // emerald-500
  "#8B5CF6", // violet-500
  "#F59E0B", // amber-500
  "#06B6D4", // cyan-500
  "#D946EF", // fuchsia-500
  "#84CC16", // lime-500
  "#F43F5E", // rose-500
  "#64748B", // slate-500
];

function fmtMoney(n) {
  return (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function TooltipBody({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-slate-900">{d.name}</div>
      <div className="mt-1 space-y-0.5 text-slate-600">
        <div>Ownership: <span className="font-medium text-slate-900 tabular-nums">{d.value.toFixed(2)}%</span></div>
        {d.committed !== undefined ? (
          <div>Committed: <span className="font-medium text-slate-900 tabular-nums">{fmtMoney(d.committed)}</span></div>
        ) : null}
        {d.pref !== undefined ? (
          <div>Pref received: <span className="font-medium text-slate-900 tabular-nums">{fmtMoney(d.pref)}</span></div>
        ) : null}
        {d.capital !== undefined ? (
          <div>Capital back: <span className="font-medium text-slate-900 tabular-nums">{fmtMoney(d.capital)}</span></div>
        ) : null}
      </div>
    </div>
  );
}

export default function CapTablePieChart({ members = [] }) {
  const [activeIdx, setActiveIdx] = useState(null);

  const { slices, totalPct } = useMemo(() => {
    if (!members.length) return { slices: [], totalPct: 0 };
    const real = members.map((m, i) => {
      const isSponsor = m.role === "sponsor";
      const color = isSponsor
        ? SPONSOR_COLOR
        : LP_PALETTE[i % LP_PALETTE.length];
      return {
        name: m.display_name || m.email || "Member",
        role: m.role,
        value: Math.max(0, Number(m.ownership_pct || 0)),
        // Defensive: /cap-table uses `committed_amount`, the
        // /syndication/partnerships/{id} detail endpoint uses `committed`.
        // Accept either so the chart works on both surfaces.
        committed: Number(m.committed_amount ?? m.committed ?? 0),
        pref: Number(m.cumulative_pref_paid || 0),
        capital: Number(m.cumulative_capital_returned || 0),
        color,
        memberId: m.member_id || m.id || `m-${i}`,
      };
    });
    const sum = real.reduce((acc, s) => acc + s.value, 0);
    const slicesOut = [...real];
    if (sum < 100 - 0.01) {
      slicesOut.push({
        name: "Unallocated",
        role: "unallocated",
        value: 100 - sum,
        color: UNALLOCATED_COLOR,
        memberId: "__unallocated__",
      });
    }
    return { slices: slicesOut, totalPct: sum };
  }, [members]);

  if (!members.length) {
    return (
      <div
        data-testid="partnership-cap-table-piechart-empty"
        className="grid h-72 place-items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500"
      >
        No members yet
      </div>
    );
  }

  return (
    <div
      data-testid="partnership-cap-table-piechart"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Cap table
          </div>
          <div className="mt-0.5 text-sm font-medium text-slate-700">
            {members.length} member{members.length === 1 ? "" : "s"} ·
            {" "}
            <span className="tabular-nums">{totalPct.toFixed(2)}%</span> allocated
          </div>
        </div>
      </div>

      <div className="relative mt-4 h-64 sm:h-72">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius="62%"
              outerRadius="90%"
              paddingAngle={1}
              stroke="none"
              onMouseEnter={(_, idx) => setActiveIdx(idx)}
              onMouseLeave={() => setActiveIdx(null)}
            >
              {slices.map((s, idx) => (
                <Cell
                  key={s.memberId}
                  fill={s.color}
                  opacity={activeIdx === null || activeIdx === idx ? 1 : 0.45}
                />
              ))}
            </Pie>
            <Tooltip content={<TooltipBody />} />
          </PieChart>
        </ResponsiveContainer>
        {/* Centre overlay — total ownership */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Allocated
          </div>
          <div className="mt-0.5 text-3xl font-bold tabular-nums text-slate-900">
            {totalPct.toFixed(0)}%
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500">of total equity</div>
        </div>
      </div>

      {/* Legend — wraps gracefully on mobile */}
      <ul className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {slices.map((s, idx) => (
          <li
            key={s.memberId}
            data-testid={`partnership-cap-table-piechart-legend-${s.memberId}`}
            className="flex items-center justify-between gap-2 rounded-lg px-2 py-1 text-xs transition-colors hover:bg-slate-50"
            onMouseEnter={() => setActiveIdx(idx)}
            onMouseLeave={() => setActiveIdx(null)}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
              <span className="truncate text-slate-700">{s.name}</span>
              {s.role === "sponsor" ? (
                <span className="ml-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-indigo-700">
                  Sponsor
                </span>
              ) : null}
            </div>
            <span className="shrink-0 tabular-nums font-medium text-slate-900">
              {s.value.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
